import { useMemo } from "react";
import { useAuth } from "../../auth/authContext.tsx";
import type { KioskCacheItem, LocationCacheItem } from "../../types/session.ts";
import { IdentitySinglePicker, type IdentitySinglePickerConfig } from "./identitySinglePicker.tsx";

interface KioskSinglePickerProps {
  value: number;
  onChange: (id: number) => void;
  disabled?: boolean;
  placeholder?: string;
  onIdEnter?: () => void;
  /** Mirror the typed search text — host can drive a sibling filter (e.g.
   *  a configured-items list shown alongside the picker). */
  onSearchChange?: (text: string) => void;
}

type EnrichedKiosk = KioskCacheItem & {
  _displayName: string;
  _secondary: string;
};

/**
 * Form field: pick a single kiosk by ID or by typing (StoreName / Address /
 * City / IP / ExternalReference). Reads from `cache.CommonData.Kiosk` joined
 * with `cache.CommonData.Location` so kiosks without their own StoreName
 * still match on the parent location's display fields — same enrichment the
 * grid-side KioskFilter performs.
 */
export function KioskSinglePicker({ value, onChange, disabled, placeholder, onIdEnter, onSearchChange }: KioskSinglePickerProps) {
  const { cache } = useAuth();

  const items = useMemo<EnrichedKiosk[]>(() => {
    const kiosks = (cache?.CommonData?.Kiosk ?? []) as KioskCacheItem[];
    const locations = (cache?.CommonData?.Location ?? []) as LocationCacheItem[];
    const locById = new Map<number, LocationCacheItem>();
    for (const l of locations) locById.set(l.LocationID, l);
    return kiosks.map(k => {
      const l = locById.get(k.LocationID);
      const displayName = k.StoreName || l?.StoreName || `Kiosk ${k.KioskID}`;
      const secondary = [
        k.Address || l?.Address, k.City || l?.City, k.District || l?.District,
      ].filter(Boolean).join(", ");
      return { ...k, _displayName: displayName, _secondary: secondary } as EnrichedKiosk;
    });
  }, [cache]);

  const config = useMemo<IdentitySinglePickerConfig<EnrichedKiosk>>(
    () => ({
      items,
      getId: (k) => k.KioskID,
      nameField: "_displayName",
      noun: "kiosk",
      matchFn: (k, q) =>
        String(k.KioskID).includes(q) ||
        k._displayName.toLowerCase().includes(q) ||
        (!!k.IpAddress && k.IpAddress.toLowerCase().includes(q)) ||
        (!!k.ExternalReference && k.ExternalReference.toLowerCase().includes(q)) ||
        k._secondary.toLowerCase().includes(q),
      secondaryFn: (k) => k._secondary,
    }),
    [items],
  );

  return (
    <IdentitySinglePicker
      value={value}
      onChange={onChange}
      config={config}
      placeholder={placeholder}
      disabled={disabled}
      onIdEnter={onIdEnter}
      onSearchChange={onSearchChange}
    />
  );
}
